/**
 * Utility functions for log parsing, formatting, and analysis
 */

/**
 * Parse a log entry from various formats
 * @param {string} logLine - Raw log line
 * @returns {Object|null} Parsed log entry or null if parsing fails
 */
export function parseLogEntry(logLine) {
  if (!logLine || typeof logLine !== 'string') {
    return null;
  }

  // Try to parse as JSON first (structured logs)
  try {
    const parsed = JSON.parse(logLine);
    if (parsed.timestamp && parsed.level && parsed.message) {
      return {
        ...parsed,
        parsed: true,
        format: 'json',
      };
    }
  } catch (e) {
    // Not JSON, try legacy format
  }

  // Try to parse legacy format: "2024-01-01 12:00:00 [ERROR]: Message"
  const legacyMatch = logLine.match(/^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}) \[(\w+)\]: (.+)$/);
  if (legacyMatch) {
    const [, timestamp, level, message] = legacyMatch;
    return {
      timestamp,
      level: level.toLowerCase(),
      message,
      parsed: true,
      format: 'legacy',
    };
  }

  // Try to parse simple format: "2024-01-01 12:00:00 ERROR Message"
  const simpleMatch = logLine.match(/^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}) (\w+) (.+)$/);
  if (simpleMatch) {
    const [, timestamp, level, message] = simpleMatch;
    return {
      timestamp,
      level: level.toLowerCase(),
      message,
      parsed: true,
      format: 'simple',
    };
  }

  return null;
}

/**
 * Format timestamp for display in Melbourne timezone
 * @param {string} timestamp - ISO timestamp or formatted timestamp (Melbourne timezone)
 * @returns {string} Formatted timestamp in Melbourne timezone
 */
export function formatTimestamp(timestamp) {
  if (!timestamp) return 'Unknown';

  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      return timestamp; // Return as-is if not a valid date
    }

    // Format in Melbourne timezone (Australia/Melbourne)
    // Note: Backend generates timestamps in Melbourne timezone
    // This ensures consistent display format regardless of client timezone
    return date.toLocaleString('en-AU', {
      timeZone: 'Australia/Melbourne',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch (e) {
    return timestamp;
  }
}

/**
 * Get color classes for log level
 * @param {string} level - Log level
 * @returns {string} CSS classes for styling
 */
export function getLevelColor(level) {
  switch (level?.toLowerCase()) {
    case 'error':
      return 'text-red-600 bg-red-50 border-red-200';
    case 'warn':
    case 'warning':
      return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    case 'info':
      return 'text-blue-600 bg-blue-50 border-blue-200';
    case 'debug':
      return 'text-gray-600 bg-gray-50 border-gray-200';
    case 'trace':
      return 'text-purple-600 bg-purple-50 border-purple-200';
    default:
      return 'text-gray-600 bg-gray-50 border-gray-200';
  }
}

/**
 * Get icon for log level
 * @param {string} level - Log level
 * @returns {string} Icon name
 */
export function getLevelIcon(level) {
  switch (level?.toLowerCase()) {
    case 'error':
      return 'AlertTriangle';
    case 'warn':
    case 'warning':
      return 'AlertTriangle';
    case 'info':
      return 'Info';
    case 'debug':
      return 'Bug';
    case 'trace':
      return 'Search';
    default:
      return 'FileText';
  }
}

/**
 * Truncate message for display
 * @param {string} message - Log message
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated message
 */
export function truncateMessage(message, maxLength = 100) {
  if (!message || typeof message !== 'string') {
    return '';
  }

  if (message.length <= maxLength) {
    return message;
  }

  return `${message.substring(0, maxLength)}...`;
}

/**
 * Extract error details from log entry
 * @param {Object} logEntry - Log entry object
 * @returns {Object} Error details
 */
export function extractErrorDetails(logEntry) {
  if (!logEntry) return null;

  const details = {
    hasStack: false,
    hasMetadata: false,
    errorType: null,
    errorCode: null,
    userId: null,
    requestId: null,
    url: null,
    method: null,
    userAgent: null,
    ip: null,
  };

  // Check for stack trace
  if (logEntry.stack) {
    details.hasStack = true;
  }

  // Check for metadata
  if (logEntry.meta && Object.keys(logEntry.meta).length > 0) {
    details.hasMetadata = true;

    // Extract common metadata fields
    if (logEntry.meta.errorType) details.errorType = logEntry.meta.errorType;
    if (logEntry.meta.errorCode) details.errorCode = logEntry.meta.errorCode;
    if (logEntry.meta.userId) details.userId = logEntry.meta.userId;
    if (logEntry.meta.requestId) details.requestId = logEntry.meta.requestId;
    if (logEntry.meta.url) details.url = logEntry.meta.url;
    if (logEntry.meta.method) details.method = logEntry.meta.method;
    if (logEntry.meta.userAgent) details.userAgent = logEntry.meta.userAgent;
    if (logEntry.meta.ip) details.ip = logEntry.meta.ip;
  }

  // Try to extract error type from message
  if (logEntry.message) {
    const errorTypeMatch = logEntry.message.match(/^(\w+Error):/);
    if (errorTypeMatch) {
      details.errorType = errorTypeMatch[1];
    }
  }

  return details;
}

/**
 * Group logs by level
 * @param {Array} logs - Array of log entries
 * @returns {Object} Grouped logs by level
 */
export function groupLogsByLevel(logs) {
  const grouped = {
    error: [],
    warn: [],
    info: [],
    debug: [],
    trace: [],
    other: [],
  };

  logs.forEach(log => {
    const level = log.level?.toLowerCase();
    if (grouped[level]) {
      grouped[level].push(log);
    } else {
      grouped.other.push(log);
    }
  });

  return grouped;
}

/**
 * Filter logs by criteria
 * @param {Array} logs - Array of log entries
 * @param {Object} filters - Filter criteria
 * @returns {Array} Filtered logs
 */
export function filterLogs(logs, filters) {
  return logs.filter(log => {
    // Level filter
    if (filters.level && filters.level !== 'all' && log.level !== filters.level) {
      return false;
    }

    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      const messageMatch = log.message?.toLowerCase().includes(searchTerm);
      const sourceMatch = log.source?.toLowerCase().includes(searchTerm);
      const metaMatch = JSON.stringify(log.meta || {})
        .toLowerCase()
        .includes(searchTerm);

      if (!messageMatch && !sourceMatch && !metaMatch) {
        return false;
      }
    }

    // Date range filter
    if (filters.startDate || filters.endDate) {
      const logDate = new Date(log.timestamp);

      if (filters.startDate && logDate < new Date(filters.startDate)) {
        return false;
      }

      if (filters.endDate && logDate > new Date(filters.endDate)) {
        return false;
      }
    }

    // Source filter
    if (filters.source && log.source !== filters.source) {
      return false;
    }

    return true;
  });
}

/**
 * Sort logs by timestamp
 * @param {Array} logs - Array of log entries
 * @param {string} order - Sort order ('asc' or 'desc')
 * @returns {Array} Sorted logs
 */
export function sortLogsByTimestamp(logs, order = 'desc') {
  return [...logs].sort((a, b) => {
    const dateA = new Date(a.timestamp);
    const dateB = new Date(b.timestamp);

    if (order === 'asc') {
      return dateA - dateB;
    } else {
      return dateB - dateA;
    }
  });
}

/**
 * Get log statistics
 * @param {Array} logs - Array of log entries
 * @returns {Object} Statistics
 */
export function getLogStatistics(logs) {
  const stats = {
    total: logs.length,
    error: 0,
    warn: 0,
    info: 0,
    debug: 0,
    trace: 0,
    other: 0,
    byHour: {},
    byDay: {},
    bySource: {},
    recentErrors: 0,
  };

  const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);

  logs.forEach(log => {
    const level = log.level?.toLowerCase();
    if (stats[level] !== undefined) {
      stats[level]++;
    } else {
      stats.other++;
    }

    // Count recent errors (last hour)
    if (level === 'error' && new Date(log.timestamp) > oneHourAgo) {
      stats.recentErrors++;
    }

    // Group by hour
    const hour = new Date(log.timestamp).getHours();
    stats.byHour[hour] = (stats.byHour[hour] || 0) + 1;

    // Group by day
    const day = new Date(log.timestamp).toDateString();
    stats.byDay[day] = (stats.byDay[day] || 0) + 1;

    // Group by source
    const source = log.source || 'unknown';
    stats.bySource[source] = (stats.bySource[source] || 0) + 1;
  });

  return stats;
}

/**
 * Export logs to different formats
 * @param {Array} logs - Array of log entries
 * @param {string} format - Export format ('json', 'csv', 'txt')
 * @returns {string} Exported data
 */
export function exportLogs(logs, format = 'json') {
  switch (format.toLowerCase()) {
    case 'csv':
      return exportToCSV(logs);
    case 'txt':
      return exportToText(logs);
    case 'json':
    default:
      return exportToJSON(logs);
  }
}

/**
 * Export logs to CSV format
 * @param {Array} logs - Array of log entries
 * @returns {string} CSV data
 */
function exportToCSV(logs) {
  const headers = ['timestamp', 'level', 'message', 'source', 'stack', 'meta'];
  const rows = logs.map(log => [
    log.timestamp,
    log.level,
    `"${(log.message || '').replace(/"/g, '""')}"`,
    log.source || '',
    `"${(log.stack || '').replace(/"/g, '""')}"`,
    `"${JSON.stringify(log.meta || {}).replace(/"/g, '""')}"`,
  ]);

  return [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
}

/**
 * Export logs to text format
 * @param {Array} logs - Array of log entries
 * @returns {string} Text data
 */
function exportToText(logs) {
  return logs
    .map(log => {
      let text = `${log.timestamp} [${log.level.toUpperCase()}]: ${log.message}`;
      if (log.source) text += ` (${log.source})`;
      if (log.stack) text += `\n${log.stack}`;
      if (log.meta && Object.keys(log.meta).length > 0) {
        text += `\nMetadata: ${JSON.stringify(log.meta, null, 2)}`;
      }
      return text;
    })
    .join('\n\n');
}

/**
 * Export logs to JSON format
 * @param {Array} logs - Array of log entries
 * @returns {string} JSON data
 */
function exportToJSON(logs) {
  return JSON.stringify(
    {
      exportedAt: new Date().toISOString(),
      total: logs.length,
      logs,
    },
    null,
    2
  );
}

/**
 * Generate log analysis report
 * @param {Array} logs - Array of log entries
 * @returns {Object} Analysis report
 */
export function generateLogAnalysis(logs) {
  const stats = getLogStatistics(logs);
  const grouped = groupLogsByLevel(logs);

  // Find most common errors
  const errorMessages = {};
  grouped.error.forEach(log => {
    const message = log.message || 'Unknown error';
    errorMessages[message] = (errorMessages[message] || 0) + 1;
  });

  const mostCommonErrors = Object.entries(errorMessages)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10)
    .map(([message, count]) => ({ message, count }));

  // Find peak hours
  const peakHours = Object.entries(stats.byHour)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5)
    .map(([hour, count]) => ({ hour: parseInt(hour), count }));

  return {
    summary: {
      totalLogs: stats.total,
      errorRate: stats.total > 0 ? ((stats.error / stats.total) * 100).toFixed(2) : 0,
      recentErrorRate: stats.total > 0 ? ((stats.recentErrors / stats.total) * 100).toFixed(2) : 0,
      mostActiveHour: peakHours[0]?.hour || 0,
    },
    breakdown: {
      byLevel: {
        error: stats.error,
        warn: stats.warn,
        info: stats.info,
        debug: stats.debug,
        trace: stats.trace,
        other: stats.other,
      },
      bySource: stats.bySource,
    },
    insights: {
      mostCommonErrors,
      peakHours,
      recentErrors: stats.recentErrors,
    },
  };
}
